<template lang="pug">
footer
  span.btn(
    @click.stop.prevent="goBack",
    @keypress.enter.stop.prevent="goBack",
    v-if="prev",
    role="button",
    tabindex="0"
  )
    i.fa.fa-chevron-left
    | Back
  span.btn(
    @click.stop.prevent="doValidate",
    @keypress.enter.stop.prevent="doValidate",
    v-if="next",
    role="button",
    tabindex="0"
  )
    | Next
    i.fa.fa-chevron-right
  span.btn(
    @click.stop.prevent="doSubmit",
    @keypress.enter.stop.prevent="doSubmit",
    v-if="!next",
    role="button",
    tabindex="0"
  )
    | Submit
    i.fa.fa-chevron-right
  span.warning(v-if="submitFail", v-for="error in errorMessages") {{error}}
</template>

<script>
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { ref, computed, nextTick } from "vue";

export default {
  name: "Footer",
  props: {
    formControls: Object,
    pushToDataLayer: Function,
  },
  setup(p) {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const errorMessages = computed(() => {
      let errorMessages;
      try {
        errorMessages = store.state.errorMessages;
      } catch (_) {
        errorMessages = [];
      }
      return errorMessages;
    });

    const submitFail = computed(() => {
      let submitFail;
      try {
        submitFail = store.state.submitFail;
      } catch (_) {
        submitFail = false;
      }
      return submitFail;
    });

    const next = computed(() => {
      let next;
      try {
        next = store.getters.nextPage(route.params.slug);
      } catch (_) {
        next = false;
      }

      return next;
    });

    const prev = computed(() => {
      let prev;
      try {
        prev = store.getters.prevPage(route.params.slug);
      } catch (_) {
        prev = false;
      }
      return prev;
    });

    return {
      errorMessages,
      next,
      prev,
      submitFail,
      goBack,
      doValidate,
      doSubmit,
    };

    function goBack() {
      router.push(prev.value.slug);
    }

    async function doValidate() {
      const { valid, errors } = await p.formControls.validate();
      const record = {};

      Object.keys(errors).forEach((field) => (record[field] = true));
      p.formControls.setTouched(record);

      await nextTick();

      if (valid) {
        if (next.value.index > store.getters.formCompletionIndex) {
          const data = {
            event: "form_action",
            formStatus: "complete-step",
          };
          //console.log(data);
          //console.log("DATALAYER -> formStatus: complete-step | step: " + next.value.index);
          window.dataLayer.push(data);
        }

        router.push(next.value.slug);
        store.commit("setFormCompletionIndex", next.value.index);

        //scroll to the top of the form
        let formTop = document.querySelector("#form_app").offsetTop;
        setTimeout(() => {
          //timeout is to allow the form to render before scrolling, otherwise window.scrollTo will not work durring render phase of vue lifecycle
          window.scrollTo({
            top: formTop - 32, // 32px is to give a little space between the top of the form and the top of the window
            behavior: "smooth",
          });
        }, 2);
      } else {
        const data = {
          event: "form_action",
          formStatus: "validate-error",
          validationError: "yes",
        };
        //console.log(data);
        //console.log("DATALAYER -> formStatus: validate-error | step: " + next.value.index);
        window.dataLayer.push(data);

        let scrollPoint = document
          .querySelector("form-error")
          .closest("form-field, form-control");
        scrollPoint.scrollIntoView({ behavior: "smooth" });
      }
    }

    async function doSubmit() {
      const { valid, errors } = await p.formControls.validate();
      const record = {};

      Object.keys(errors).forEach((field) => (record[field] = true));
      p.formControls.setTouched(record);

      if (valid) {
        store.commit("setLoading", true);

        const confirmation = await store.dispatch(
          "submitData",
          store.getters.submitEndpoint
        );

        console.log(confirmation);

        if (confirmation == "Fail" || (confirmation.errors && Array.isArray(confirmation.errors) && confirmation.errors.length)) {
          if (confirmation.errors && Array.isArray(confirmation.errors) && confirmation.errors.length) {
            store.state.errorMessages = confirmation.errors;
          } else {
            store.state.errorMessages = [
              "Form submission failed. Please try again later.",
            ];
          }

          store.commit("setSubmitFail", true);
          store.commit("setLoading", false);

          window.dataLayer.push({
            event: "form_action",
            formStatus: "fail",
          });
        } else {
          store.commit("setSubmitFail", false);

          const data = {
            event: "form_action",
            formStatus: "success",
            slug: "thank-you",
            navLabel: "Thank You",
            stepName: "Thank You",
            step: store.getters.pages.length + 1,
          };

          window.dataLayer.push(data);

          if (confirmation.redirect) {
            window.location.href = confirmation.redirect;
          }
        }
      } else {
        const data = {
          event: "form_action",
          formStatus: "validate-error",
          validationError: "yes",
        };
        
        window.dataLayer.push(data);
      }
    }
  },
};
</script>

<style scoped lang="scss">
footer {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 1rem;
  margin: 0;
  border-top: 2px solid #adb4b9;
  padding: 2rem 0;
  z-index: -1;
}

span.warning {
  color: red;
  margin-top: 5px;
}

span.btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.888rem;
  line-height: 1;
  margin: 0;
  cursor: pointer;
  border-radius: 0;
  background: #4e626d;
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px $sky;
  }
  i {
    font-size: 0.5rem;
    &::before {
      line-height: 1;
    }
  }
}
</style>
